// M004-F0744 (docs/design/M004-F0744-F0747-Envelope-Workspace.md sections 4, 5, 10) — pure model for the My Envelopes workspace.
// T-M004-ENVUX-003 proves this file (totals, filter grammar, Unsorted pin); T-M004-ENVUX-001 proves the optimistic create transitions.
//
// Tolerance: the Spec 1.4.0 additions (kind, locked, claim, totals, ocr, merchant, receipt_date, vat_minor, reference) may be ABSENT
// (Spec 1.2.0/1.3.0 Ledgers). Every reader here treats absent as "not known", never as a guess:
//   - no `kind`            -> "standard" (no Unsorted row is invented);
//   - no `locked`/`claim`  -> not locked;
//   - no `totals`          -> totals are computed here from the loaded receipts, and are unknown for envelopes whose receipts are not loaded;
//   - no `ocr`             -> a receipt is confirmed exactly when it carries an `amount` (the F0636 behaviour: the user typed it).
// Totals sum CONFIRMED amounts only (section 5): a receipt without a confirmed amount counts in "N to check" and never in the sum.
import { sortEnvelopes, statusChip } from './envelopeModel';

export const UNSORTED_LABEL = 'Unsorted';
export const NO_ENVELOPE_RETURNED_TEXT = 'The envelope could not be opened. Please try again.';
export const MAX_ENVELOPE_NAME = 120; // Spec EnvelopeName maxLength; the Ledger's sentence is shown verbatim for everything else.
export const CREATE_RETRY_DELAY_MS = 250;
export const OFFLINE_RETRY_MS = 30000;
export const ONLINE_REFRESH_MS = 30000; // M004-F0793: interim poll while the page is visible; replaced by the Ledger changes feed (Spec 1.7.0)
export const CACHE_PREFIX = 'studioaeye.envelopes.cache.v1';
export const VIEW_KEY = 'studioaeye.envelopes.view.v1';

// ---- envelope records ------------------------------------------------------------------------------------------------------------

const CLAIM_LOCKED_STATES = new Set(['submitted', 'approved', 'reimbursed']);
const CLAIM_CHIPS = { submitted: 'Submitted', returned: 'Returned', approved: 'Approved', reimbursed: 'Reimbursed' };

export const normaliseEnvelope = (record) => {
  const claim = record?.claim && typeof record.claim === 'object' ? record.claim : null;
  const locked = typeof record?.locked === 'boolean' ? record.locked : Boolean(claim && CLAIM_LOCKED_STATES.has(claim.state));
  return {
    ...record,
    kind: record?.kind === 'unsorted' ? 'unsorted' : 'standard',
    locked,
    claim,
    totals: Array.isArray(record?.totals) ? record.totals : null,
    counts: record?.counts || { receipts: 0, bytes: 0 },
  };
};

export const isUnsorted = (record) => record?.kind === 'unsorted';

// The state chip shown when an envelope is not plainly open: Submitted / Returned / Approved / Reimbursed / Closed (section 3.1).
export const stateChipFor = (record) => {
  if (!record) return null;
  if (record.status === 'deleted') return statusChip(record); // M004-F0636 §5: the server countdown, never a bare 'Deleted'
  if (record.claim && CLAIM_CHIPS[record.claim.state]) {
    return { label: CLAIM_CHIPS[record.claim.state], tone: record.claim.state === 'returned' ? 'warning' : 'locked' };
  }
  if (record.status === 'closed') return { label: 'Closed', tone: 'closed' };
  return null;
};

// Unsorted is pinned first; the rest keep the F0636 order (open newest-first, deleted last).
export const orderEnvelopes = (records) => {
  const list = (Array.isArray(records) ? records : []).map(normaliseEnvelope);
  const pinned = list.filter(isUnsorted);
  const rest = sortEnvelopes(list.filter((record) => !isUnsorted(record)));
  const pending = rest.filter((record) => record.pending);
  return pinned.concat(pending, rest.filter((record) => !record.pending));
};

export const rowKey = (record) => record.clientKey || record.envelope_id;

// ---- optimistic create (section 4) -----------------------------------------------------------------------------------------------

export const newClientKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return `tmp-${crypto.randomUUID()}`;
  return `tmp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

export const makeTempEnvelope = (name, clientKey) => normaliseEnvelope({
  envelope_id: clientKey, clientKey, pending: true, name, notes: '', status: 'open',
  counts: { receipts: 0, bytes: 0 }, updated_at: new Date().toISOString(),
});

export const addTemp = (records, temp) => orderEnvelopes((records || []).concat(temp));

// The server's record takes the temp row's place and KEEPS its clientKey, so the React key (and DOM node) never changes: no flicker.
export const reconcileCreate = (records, clientKey, serverRecord) => {
  const merged = { ...normaliseEnvelope(serverRecord), clientKey, pending: false };
  const without = (records || []).filter((item) => item.clientKey !== clientKey && item.envelope_id !== merged.envelope_id);
  return orderEnvelopes(without.concat(merged));
};

export const rollbackCreate = (records, clientKey) => (records || []).filter((item) => item.clientKey !== clientKey);

// ---- receipts and totals ---------------------------------------------------------------------------------------------------------

export const confirmedAmount = (receipt) => {
  const amount = receipt?.amount;
  if (!amount || !Number.isFinite(Number(amount.minor))) return null;
  return { minor: Number(amount.minor), currency: String(amount.currency || '').toUpperCase() || 'USD' };
};

// 'confirmed' | 'needs_checking' | 'reading' | 'failed' — what the filter menu and the chip read.
export const receiptStatus = (receipt) => {
  const ocr = receipt?.ocr?.status;
  if (ocr === 'queued' || ocr === 'reading') return 'reading';
  if (ocr === 'failed' && !confirmedAmount(receipt)) return 'failed';
  if (confirmedAmount(receipt)) return 'confirmed';
  if (ocr === 'suggested') return 'suggested';
  if (ocr === 'unavailable') return 'unavailable';
  return 'needs_checking';
};

// VAT is the 1.4.0 receipt field `vat_minor`, else the 1.3.0 `amount.vat_minor` (VAT inside the amount); absent means unknown = 0 here.
export const receiptVat = (receipt) => {
  const own = Number(receipt?.vat_minor);
  if (Number.isFinite(own) && receipt?.vat_minor !== null && receipt?.vat_minor !== undefined) return own;
  const inner = Number(receipt?.amount?.vat_minor);
  return Number.isFinite(inner) && receipt?.amount?.vat_minor !== null && receipt?.amount?.vat_minor !== undefined ? inner : 0;
};

export const computeTotals = (receipts) => {
  const list = Array.isArray(receipts) ? receipts : [];
  const byCurrency = new Map();
  let toCheck = 0;
  list.forEach((receipt) => {
    const amount = confirmedAmount(receipt);
    if (!amount) { toCheck += 1; return; }
    const entry = byCurrency.get(amount.currency) || { currency: amount.currency, count: 0, total_minor: 0, vat_minor: 0 };
    entry.count += 1;
    entry.total_minor += amount.minor;
    entry.vat_minor += receiptVat(receipt);
    byCurrency.set(amount.currency, entry);
  });
  return { count: list.length, toCheck, currencies: Array.from(byCurrency.values()).sort((a, b) => a.currency.localeCompare(b.currency)), source: 'computed' };
};

// Server `totals: [{currency,count,total_minor,vat_minor,unconfirmed}]` (Spec 1.4.0 list). `unconfirmed` is read as receipts still to check.
export const totalsFromServer = (totals, receiptCount) => {
  if (!Array.isArray(totals)) return null;
  const toCheck = totals.reduce((sum, row) => sum + (Number(row.unconfirmed) || 0), 0);
  return {
    count: Number.isFinite(Number(receiptCount)) ? Number(receiptCount) : totals.reduce((sum, row) => sum + (Number(row.count) || 0), 0) + toCheck,
    toCheck,
    currencies: totals.filter((row) => Number(row.count) > 0 || Number(row.total_minor) !== 0)
      .map((row) => ({ currency: String(row.currency || '').toUpperCase(), count: Number(row.count) || 0, total_minor: Number(row.total_minor) || 0, vat_minor: Number(row.vat_minor) || 0 })),
    source: 'server',
  };
};

// Loaded receipts win (they carry optimistic and just-saved edits); else the server's totals; else unknown (null) — never a guess.
export const totalsFor = (record, receipts) => {
  if (Array.isArray(receipts)) return computeTotals(receipts);
  return totalsFromServer(record?.totals, record?.counts?.receipts);
};

export const formatMoney = (minor, currency) => {
  const value = Number(minor) || 0;
  const abs = Math.abs(value);
  const whole = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${currency ? `${currency} ` : ''}${value < 0 ? '-' : ''}${whole}.${String(abs % 100).padStart(2, '0')}`;
};

export const describeTotals = (totals) => {
  if (!totals) return [];
  return totals.currencies.map((row) => formatMoney(row.total_minor, row.currency));
};

// ---- filter and search (section 5) -----------------------------------------------------------------------------------------------

export const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'needs_checking', label: 'Needs checking' },
  { value: 'reading', label: 'Reading' },
  { value: 'failed', label: 'Failed' },
  { value: 'confirmed', label: 'Confirmed' },
];

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

export const receiptDate = (receipt) => String(receipt?.receipt_date || receipt?.captured_at || '').slice(0, 10);

export const receiptTitle = (receipt) => receipt?.merchant || receipt?.ocr?.suggestion?.merchant || receipt?.original_filename || 'Receipt';

// Query grammar: `>50` / `<=20` / `49.99` (amount), `2026-09` / `2026-09-14` / `14 Sep` (date), anything else: every word must appear in
// merchant, note, filename or order/reference number. A bare number also matches text (an order number), an amount equal to it, and a date.
export const parseQuery = (text) => {
  const query = String(text || '').trim().toLowerCase();
  if (!query) return { kind: 'none' };
  const cmp = /^(>=|<=|>|<)\s*(\d+(?:\.\d{1,2})?)$/.exec(query);
  if (cmp) return { kind: 'amount', op: cmp[1], minor: Math.round(Number(cmp[2]) * 100) };
  const iso = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(query);
  if (iso) return { kind: 'date', prefix: iso[3] ? `${iso[1]}-${iso[2]}-${iso[3]}` : `${iso[1]}-${iso[2]}` };
  const dm = /^(\d{1,2})\s+([a-z]{3})[a-z]*$/.exec(query);
  if (dm && MONTHS.includes(dm[2])) return { kind: 'date', day: dm[1].padStart(2, '0'), month: String(MONTHS.indexOf(dm[2]) + 1).padStart(2, '0') };
  const num = /^\d+(?:\.\d{1,2})?$/.exec(query);
  if (num) return { kind: 'number', minor: Math.round(Number(query) * 100), text: query };
  return { kind: 'text', words: query.split(/\s+/) };
};

const haystack = (receipt) => [receiptTitle(receipt), receipt?.merchant, receipt?.note, receipt?.original_filename, receipt?.reference]
  .filter(Boolean).join(' ').toLowerCase();

const matchesAmount = (receipt, op, minor) => {
  const amount = confirmedAmount(receipt);
  if (!amount) return false;
  switch (op) {
    case '>': return amount.minor > minor;
    case '>=': return amount.minor >= minor;
    case '<': return amount.minor < minor;
    case '<=': return amount.minor <= minor;
    default: return amount.minor === minor;
  }
};

const matchesDate = (receipt, parsed) => {
  const date = receiptDate(receipt);
  if (!date) return false;
  if (parsed.prefix) return date.startsWith(parsed.prefix);
  return date.slice(8, 10) === parsed.day && date.slice(5, 7) === parsed.month;
};

export const matchesQuery = (receipt, parsed) => {
  switch (parsed.kind) {
    case 'none': return true;
    case 'amount': return matchesAmount(receipt, parsed.op, parsed.minor);
    case 'date': return matchesDate(receipt, parsed);
    case 'number': return matchesAmount(receipt, '=', parsed.minor) || haystack(receipt).includes(parsed.text);
    default: { const hay = haystack(receipt); return parsed.words.every((word) => hay.includes(word)); }
  }
};

// "Needs checking" is everything the user still has to look at: suggested, read-off and not-yet-read-at-all receipts without a confirmed amount.
const statusMatches = (filter, status) => (filter === 'needs_checking' ? ['needs_checking', 'suggested', 'unavailable'].includes(status) : filter === 'failed' ? status === 'failed' : status === filter);

// `ids` (M004-F0747 "Show them"): only the receipts a claim issue names.
export const filterReceipts = (receipts, { query = '', status = 'all', ids = null } = {}) => {
  const parsed = parseQuery(query);
  const only = Array.isArray(ids) ? new Set(ids) : null;
  return (Array.isArray(receipts) ? receipts : [])
    .filter((receipt) => !only || only.has(receipt?.receipt_document_id))
    .filter((receipt) => status === 'all' || statusMatches(status, receiptStatus(receipt)))
    .filter((receipt) => matchesQuery(receipt, parsed));
};

export const resultCountText = (shown, total) => {
  if (shown === total) return `${total} ${total === 1 ? 'receipt' : 'receipts'}`;
  return `${shown} of ${total} ${total === 1 ? 'receipt' : 'receipts'} shown`;
};

// ---- Ledger-down cached copy (owner ruling: keep the cached view; section 10) ---------------------------------------------------

const storage = () => { try { return window.localStorage; } catch { return null; } };
export const cacheKey = (userKey, orgId) => `${CACHE_PREFIX}.${userKey || 'anon'}.${orgId}`;

export const readCache = (userKey, orgId) => {
  try {
    const raw = storage()?.getItem(cacheKey(userKey, orgId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && Array.isArray(parsed.envelopes) ? parsed : null;
  } catch { return null; }
};

export const writeCache = (userKey, orgId, snapshot) => {
  try { storage()?.setItem(cacheKey(userKey, orgId), JSON.stringify({ ...snapshot, savedAt: new Date().toISOString() })); } catch { /* Storage is optional. */ }
};

export const readView = () => { try { return storage()?.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'; } catch { return 'grid'; } };
export const writeView = (view) => { try { storage()?.setItem(VIEW_KEY, view); } catch { /* optional */ } };

export const formatSavedAt = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'earlier';
  return `${date.toISOString().slice(0, 10)} ${date.toISOString().slice(11, 16)} UTC`;
};

// A failure that proves the Ledger/transport is unreachable (no HTTP answer, or a gateway/availability status), as opposed to a refusal.
export const isUnreachable = (cause) => {
  const status = Number(cause?.response?.status) || 0;
  const code = cause?.response?.data?.error;
  return !cause?.response || status === 502 || status === 503 || status === 504 || code === 'LEDGER_NODE_OFFLINE' || code === 'LEDGER_UNAVAILABLE';
};

// ---- tile presentation -----------------------------------------------------------------------------------------------------------

const CHIPS = {
  confirmed: { label: 'Confirmed', tone: 'ok' },
  needs_checking: { label: 'Needs checking', tone: 'warn' },
  reading: { label: 'Reading…', tone: 'info' },
  suggested: { label: 'Suggested', tone: 'warn' },
  failed: { label: 'Couldn\'t read — enter details', tone: 'bad' },
  unavailable: { label: 'Automatic reading is off — enter details', tone: 'bad' },
};
export const receiptChip = (receipt) => CHIPS[receiptStatus(receipt)];

// Merchant: confirmed, else the OCR suggestion (muted, flagged `suggested`), else the filename.
export const receiptHeadline = (receipt) => {
  if (receipt?.merchant) return { text: receipt.merchant, suggested: false };
  const suggested = receipt?.ocr?.suggestion?.merchant;
  if (suggested) return { text: suggested, suggested: true };
  return { text: receipt?.original_filename || 'Receipt', suggested: false };
};

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const formatShortDate = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!match) return '';
  return `${Number(match[3])} ${SHORT_MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

export const confirmedAmountText = (receipt) => {
  const amount = confirmedAmount(receipt);
  return amount ? formatMoney(amount.minor, amount.currency) : '';
};

// The currency a bare amount ("12.50") means in this envelope: the currency its confirmed receipts already use, else the default.
export const defaultCurrencyFor = (receipts, fallback = 'GBP') => {
  const first = (Array.isArray(receipts) ? receipts : []).map(confirmedAmount).find(Boolean);
  return first ? first.currency : fallback;
};

// M004-F0793: the open envelope's detail is re-got only when the list says it changed (its updated_at differs from the one last loaded),
// or when the list carries no updated_at at all (then there is nothing to compare). `lastLoaded` is undefined when never loaded.
export const detailIsStale = (record, lastLoaded) => {
  if (!record) return false;
  if (!record.updated_at) return true;
  return record.updated_at !== lastLoaded;
};
